import * as React from "react";
import { LayoutGrid, LogOut, MonitorSmartphone, Palette, Plus, Shield, Sparkles, Type, Users } from "lucide-react";
import { NavLink, useSearchParams } from "react-router-dom";

import { cn } from "@/lib/utils";
import { usePortal } from "@/features/portal/portal-context";
import { BrandSwitcher } from "@/features/portal/brand-switcher";
import { ReportIssue } from "@/features/portal/report-issue";
import { brandPath, type BrandSection } from "@/features/portal/navigation";

const NAV: { section: BrandSection; label: string; icon: typeof Palette }[] = [
  { section: "theming", label: "Theming", icon: Palette },
  { section: "terminology", label: "Terminology", icon: Type },
  { section: "members", label: "Members", icon: Users },
  { section: "demo", label: "Demo", icon: MonitorSmartphone },
];

/** Round profile picture, falling back to the user's initials. */
function UserAvatar({ name, avatarUrl }: { name: string; avatarUrl: string }) {
  const [broken, setBroken] = React.useState(false);
  const initials = name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  if (avatarUrl && !broken) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        onError={() => setBroken(true)}
        className="size-8 shrink-0 rounded-full border border-border object-cover"
      />
    );
  }

  return (
    <div className="grid size-8 shrink-0 place-items-center rounded-full border border-border bg-panel-2 text-xs font-bold text-muted-foreground">
      {initials}
    </div>
  );
}

export function Sidebar() {
  const { user, logout, brands, activeBrand, canCreateBrand, isSuperAdmin } = usePortal();
  const [searchParams] = useSearchParams();
  const hasBrands = brands.length > 0;
  // Nav targets the active brand (or the first one when none is active yet, e.g.
  // on the create flow). Carry the current `?theme` so the selected theme
  // persists as you move between a brand's sections.
  const navBrand = activeBrand ?? brands[0];
  const themeParam = searchParams.get("theme");

  return (
    <aside className="sticky top-0 flex h-screen w-[264px] shrink-0 flex-col border-r border-border bg-bg-2 p-4">
      <div className="flex items-center gap-2.5 px-1.5 pb-4 pt-1">
        <div className="grid size-[30px] place-items-center rounded-[9px] bg-brand-gradient">
          <Sparkles className="size-[17px] text-white" />
        </div>
        <div>
          <div className="text-[13.5px] font-extrabold leading-none text-foreground">
            Partner Portal
          </div>
          <div className="mt-0.5 text-[10.5px] text-text-3">by pop.in</div>
        </div>
      </div>

      <BrandSwitcher />

      {hasBrands && navBrand ? (
        <nav className="mt-[22px] flex flex-col gap-[3px]">
          <div className="px-2 pb-2 text-[10.5px] font-bold uppercase tracking-[.06em] text-text-3">
            Configure
          </div>
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.section}
                // Carry `?theme` on every section so the selected theme persists
                // as you move between a brand's screens — this also keeps the
                // unsaved-changes blocker's draft key stable, so flipping sections
                // never spuriously prompts.
                to={brandPath(navBrand.id, n.section, themeParam)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-[11px] rounded-[10px] border px-[11px] py-2.5 text-[13.5px] font-semibold transition-all",
                    isActive
                      ? "border-border bg-card text-foreground shadow-portal"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn("size-[18px]", isActive ? "text-brand" : "text-text-3")} />
                    {n.label}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
      ) : (
        // No brands yet — there's nothing to configure, so we surface a create
        // prompt instead of the (empty, unusable) editor nav.
        canCreateBrand && (
          <div className="mt-[22px] rounded-xl border border-dashed border-border-strong bg-card p-4 text-center">
            <div className="mx-auto mb-2.5 grid size-9 place-items-center rounded-[10px] bg-brand-gradient">
              <Sparkles className="size-[18px] text-white" />
            </div>
            <div className="text-[13px] font-bold text-foreground">No brands yet</div>
            <p className="mb-3 mt-1 text-[11.5px] leading-snug text-text-3">
              Create your first white-labelled app to start theming.
            </p>
            <NavLink
              to="/create"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-[10px] bg-brand-gradient px-3 py-2 text-[13px] font-semibold text-white"
            >
              <Plus className="size-4" /> Create a brand
            </NavLink>
          </div>
        )
      )}

      {/* Admin-only: cross-brand directory. Hidden for non-super-admins, and the
          route is guarded too (RequireSuperAdmin) for direct-URL access. */}
      {isSuperAdmin && (
        <nav className="mt-[22px] flex flex-col gap-[3px]">
          <div className="flex items-center gap-1.5 px-2 pb-2 text-[10.5px] font-bold uppercase tracking-[.06em] text-text-3">
            <Shield className="size-3" /> Admin
          </div>
          <NavLink
            to="/admin/brands"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-[11px] rounded-[10px] border px-[11px] py-2.5 text-[13.5px] font-semibold transition-all",
                isActive
                  ? "border-border bg-card text-foreground shadow-portal"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )
            }
          >
            {({ isActive }) => (
              <>
                <LayoutGrid className={cn("size-[18px]", isActive ? "text-brand" : "text-text-3")} />
                All brands
                <span className="ml-auto rounded-md border border-border-strong bg-card px-1.5 py-0.5 text-[9px] font-extrabold tracking-[.06em] text-brand">
                  ADMIN
                </span>
              </>
            )}
          </NavLink>
        </nav>
      )}

      <div className="flex-1" />

      <ReportIssue />

      <div className="flex items-center gap-2.5 border-t border-border pt-3">
        <UserAvatar name={user.name} avatarUrl={user.avatarUrl} />
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-foreground">{user.name}</div>
          <div className="truncate text-[11px] text-text-3">{user.email}</div>
        </div>
        <button
          onClick={logout}
          title="Sign out"
          className="grid cursor-pointer place-items-center rounded-lg p-1.5 text-text-3 transition-colors hover:text-foreground"
        >
          <LogOut className="size-[17px]" />
        </button>
      </div>
    </aside>
  );
}
